import { useMemo } from 'react';
import { InterpolatedJnxTemplate } from '../../../util/jnx';

/**
 * Evaluates a text value (e.g. `ui:tooltipText`, a field's label/title) as a JSONata-interpolated
 * template (`$expr` / `${expr}`), so it can reference form data instead of only static strings.
 * Plain text with no interpolation markers passes through unchanged.
 *
 * `extraBindings` (e.g. `{ currentEntity }`) are merged into the eval bindings, so expressions
 * can reach data that lives outside `rootFormData` — e.g. `${$currentEntity.data.reportingWindow.data.startDate}`.
 */
export default function useInterpolatedText(
  text: string | undefined,
  rootFormData: any,
  dataPath: string = '',
  formContext?: any,
  extraBindings?: Record<string, any>
): string {
  const jnx = useMemo(() => (text ? new InterpolatedJnxTemplate(text) : null), [text]);
  const hasInterpolation = jnx?.hasInterpolation() ?? false;

  return useMemo(() => {
    // No `$expr` to resolve — skip the JSONata evaluate() call entirely instead of re-running
    // it on every rootFormData change, which matters when hundreds of fields carry static text.
    if (!hasInterpolation) return text || '';

    return jnx!.eval(rootFormData || {}, dataPath, {
      rootData: rootFormData,
      formContext,
      ...extraBindings,
    });
  }, [jnx, hasInterpolation, text, rootFormData, dataPath, formContext, extraBindings]);
}
